import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import logger from "../utils/logger";
import { 
  RefreshCw, 
  Trash2, 
  Phone, 
  MapPin, 
  User,
  Clock,
  ArrowLeft,
  Filter,
  Save,
  FileText,
  Mail,
  Home,
  ChevronDown,
  ChevronUp,
  Tag,
  TrendingUp,
  CheckCircle,
  Camera,
  Video,
  Download,
  ExternalLink,
  X
} from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  new: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  contacted: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  qualified: "bg-green-500/20 text-green-400 border-green-500/30",
  closed: "bg-purple-500/20 text-purple-400 border-purple-500/30"
};

const motivationColors = {
  high: "bg-red-500/20 text-red-400 border-red-500/30",
  medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  low: "bg-green-500/20 text-green-400 border-green-500/30"
};

const getMotivationLevel = (score) => {
  if (score >= 50) return 'high';
  if (score >= 25) return 'medium';
  return 'low';
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Photo Gallery Component with lightbox
const PhotoGallery = ({ photos }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  // Group photos by category
  const photosByCategory = photos.reduce((acc, photo) => {
    const cat = photo.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(photo);
    return acc;
  }, {});
  
  const categoryLabels = {
    front_of_house: '🏠 Front',
    back_of_house: '🏡 Back',
    left_side: '➡️ Left Side',
    right_side: '⬅️ Right Side',
    living_room: '🛋️ Living Room',
    kitchen: '🍳 Kitchen',
    bedrooms: '🛏️ Bedrooms',
    bathrooms: '🚿 Bathrooms',
    garage: '🚗 Garage',
    basement_crawl_space: '🏚️ Basement',
    mechanical_room: '🔧 Mechanical',
    damaged_areas: '⚠️ Damaged',
    other: '📷 Other'
  };
  
  return (
    <>
      <div className="space-y-4">
        {Object.entries(photosByCategory).map(([category, categoryPhotos]) => (
          <div key={category}>
            <span className="text-xs text-[#666] mb-1 block">{categoryLabels[category] || category}</span>
            <div className="flex flex-wrap gap-2">
              {categoryPhotos.map((photo, idx) => (
                <button
                  key={photo.id || idx}
                  onClick={() => setSelectedPhoto(photo)}
                  className="w-16 h-16 border border-white/10 overflow-hidden hover:border-[#D4AF37] transition-colors"
                >
                  <img
                    src={`${API}/photos/${photo.storage_path?.replace('knotts-investments/photos/', '')}`}
                    alt={photo.filename || `Photo ${idx + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Lightbox */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 text-white hover:text-[#D4AF37]"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={`${API}/photos/${selectedPhoto.storage_path?.replace('knotts-investments/photos/', '')}`}
            alt={selectedPhoto.filename || 'Photo'}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2">
            {categoryLabels[selectedPhoto.category] || 'Photo'} - {selectedPhoto.filename}
          </div>
        </div>
      )}
    </>
  );
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/questionnaire`),
        axios.get(`${API}/questionnaire/stats/summary`)
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      logger.error("Error fetching leads:", error);
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/questionnaire/${id}/status?status=${status}`);
      toast.success("Status updated");
      fetchLeads();
    } catch (error) {
      logger.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const updateNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/questionnaire/${id}/notes`, { notes });
      toast.success("Notes saved");
      fetchLeads();
    } catch (error) {
      logger.error("Error updating notes:", error);
      toast.error("Failed to save notes");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    
    try {
      await axios.delete(`${API}/questionnaire/${id}`);
      toast.success("Lead deleted");
      fetchLeads();
    } catch (error) {
      logger.error("Error deleting lead:", error);
      toast.error("Failed to delete lead");
    }
  };

  const getFilteredLeads = () => {
    if (filterStatus === "all") {
      return leads;
    }
    if (filterStatus === "high_motivation") {
      return leads.filter(lead => getMotivationLevel(lead.motivation_score) === 'high');
    }
    return leads.filter(lead => lead.status === filterStatus);
  };

  const filteredLeads = getFilteredLeads();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 sm:p-8" data-testid="admin-leads-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link 
              to="/" 
              className="text-[#A3A3A3] hover:text-white transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-2xl sm:text-3xl font-bold">
              Lead <span className="gold-text">Management</span>
            </h1>
          </div>
          <button 
            onClick={fetchLeads}
            className="btn-gold px-4 py-2 flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <StatCard label="Total" value={stats.total} color="text-white" />
            <StatCard label="New" value={stats.new} color="text-blue-400" />
            <StatCard label="Contacted" value={stats.contacted} color="text-yellow-400" />
            <StatCard label="Qualified" value={stats.qualified} color="text-green-400" />
            <StatCard label="Closed" value={stats.closed} color="text-purple-400" />
            <StatCard label="High Motivation" value={stats.high_motivation} color="text-red-400" icon={TrendingUp} />
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-4 mb-6 overflow-x-auto pb-2">
          <Filter className="w-5 h-5 text-[#A3A3A3] flex-shrink-0" />
          <div className="flex gap-2 flex-wrap">
            {[
              { key: "all", label: "All" },
              { key: "new", label: "New" },
              { key: "contacted", label: "Contacted" },
              { key: "qualified", label: "Qualified" },
              { key: "closed", label: "Closed" },
              { key: "high_motivation", label: "High Motivation" }
            ].map(filter => (
              <button
                key={filter.key}
                onClick={() => setFilterStatus(filter.key)}
                className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  filterStatus === filter.key 
                    ? 'bg-[#D4AF37] text-black' 
                    : 'bg-[#141414] text-[#A3A3A3] hover:text-white border border-white/10'
                }`}
                data-testid={`filter-${filter.key}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Leads List */}
        {loading && (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
            <p className="mt-4 text-[#A3A3A3]">Loading leads...</p>
          </div>
        )}
        
        {!loading && filteredLeads.length === 0 && (
          <div className="text-center py-12 bg-[#141414] border border-white/10">
            <p className="text-[#A3A3A3]">No leads found</p>
          </div>
        )}
        
        {!loading && filteredLeads.length > 0 && (
          <div className="space-y-4">
            {filteredLeads.map(lead => (
              <LeadCard 
                key={lead.id} 
                lead={lead} 
                onUpdateStatus={updateStatus}
                onUpdateNotes={updateNotes}
                onDelete={deleteLead}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color, icon: Icon }) => (
  <div className="bg-[#141414] border border-white/10 p-4 text-center">
    <div className="flex items-center justify-center gap-2">
      {Icon && <Icon className={`w-5 h-5 ${color}`} />}
      <p className={`text-2xl sm:text-3xl font-bold ${color}`}>{value}</p>
    </div>
    <p className="text-[#A3A3A3] text-sm">{label}</p>
  </div>
);

const LeadCard = ({ lead, onUpdateStatus, onUpdateNotes, onDelete }) => {
  const [notes, setNotes] = useState(lead.notes || "");
  const [showDetails, setShowDetails] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveNotes = async () => {
    setIsSaving(true);
    await onUpdateNotes(lead.id, notes);
    setIsSaving(false);
  };

  const cleanPhone = lead.phone_number?.replace(/\D/g, '') || '';
  const motivationLevel = getMotivationLevel(lead.motivation_score);

  return (
    <div 
      className="bg-[#141414] border border-white/10 p-4 sm:p-6 hover:border-[#D4AF37]/30 transition-colors"
      data-testid={`lead-card-${lead.id}`}
    >
      {/* Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <User className="w-5 h-5 text-[#D4AF37]" />
          <span className="text-lg font-semibold">{lead.full_name}</span>
          <span className={`px-3 py-1 text-xs font-medium border ${statusColors[lead.status]}`}>
            {lead.status.toUpperCase()}
          </span>
          <span className={`px-3 py-1 text-xs font-medium border ${motivationColors[motivationLevel]}`}>
            {lead.motivation_score}% Motivated
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={lead.status}
            onChange={(e) => onUpdateStatus(lead.id, e.target.value)}
            className="bg-[#0A0A0A] border border-white/20 text-white px-3 py-2 text-sm"
            data-testid={`status-select-${lead.id}`}
          >
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="closed">Closed</option>
          </select>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className={`p-2 transition-colors ${showDetails ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#A3A3A3] hover:text-[#D4AF37]'}`}
            title="View Details"
            data-testid={`toggle-details-${lead.id}`}
          >
            {showDetails ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setShowNotes(!showNotes)}
            className={`p-2 transition-colors ${showNotes ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#A3A3A3] hover:text-[#D4AF37]'}`}
            title="Toggle Notes"
            data-testid={`toggle-notes-${lead.id}`}
          >
            <FileText className="w-5 h-5" />
          </button>
          <button
            onClick={() => onDelete(lead.id)}
            className="p-2 text-red-400 hover:bg-red-500/10 transition-colors"
            data-testid={`delete-lead-${lead.id}`}
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Contact Info */}
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Phone className="w-4 h-4 text-[#A3A3A3]" />
          <a href={`tel:${cleanPhone}`} className="text-[#D4AF37] hover:text-[#E5C158]">{lead.phone_number}</a>
          <a 
            href={`tel:${cleanPhone}`} 
            className="px-2 py-1 bg-green-600 hover:bg-green-500 text-white text-xs font-medium transition-colors"
            data-testid={`call-btn-${lead.id}`}
          >
            Call
          </a>
          <a 
            href={`sms:${cleanPhone}`} 
            className="px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium transition-colors"
            data-testid={`text-btn-${lead.id}`}
          >
            Text
          </a>
        </div>
        <div className="flex items-center gap-3 text-[#A3A3A3]">
          <Mail className="w-4 h-4" />
          <a 
            href={`mailto:${lead.email}`} 
            className="hover:text-[#D4AF37] transition-colors"
            data-testid={`email-link-${lead.id}`}
          >
            {lead.email}
          </a>
        </div>
      </div>

      <div className="flex items-center gap-3 text-[#A3A3A3] mb-2">
        <MapPin className="w-4 h-4" />
        <span>{lead.property_address}, {lead.city}, {lead.state} {lead.zip_code}</span>
      </div>

      <div className="flex items-center gap-3 text-[#A3A3A3] text-sm mb-4">
        <Clock className="w-4 h-4" />
        <span>{formatDate(lead.created_at)}</span>
      </div>

      {/* Motivation Tags */}
      {lead.tags && lead.tags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <Tag className="w-4 h-4 text-[#D4AF37]" />
          {lead.tags.map((tag) => (
            <span key={`tag-${tag}`} className="px-2 py-1 text-xs bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Expanded Details - All Questionnaire Answers */}
      {showDetails && (
        <div className="border-t border-white/10 pt-4 mt-4 space-y-6">
          {/* Contact Information */}
          <div>
            <h3 className="text-[#D4AF37] font-semibold mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Contact Information
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <DetailItem label="Full Name" value={lead.full_name} />
              <DetailItem label="Phone Number" value={lead.phone_number} />
              <DetailItem label="Email Address" value={lead.email} />
            </div>
          </div>

          {/* Property Information */}
          <div>
            <h3 className="text-[#D4AF37] font-semibold mb-3 flex items-center gap-2">
              <Home className="w-4 h-4" />
              {lead.property_type === 'Vacant Land' ? 'Land Information' : 'Property Information'}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <DetailItem label="Property Address" value={lead.property_address} />
              <DetailItem label="City" value={lead.city} />
              <DetailItem label="State" value={lead.state} />
              <DetailItem label="ZIP Code" value={lead.zip_code} />
              <DetailItem label="Property Type" value={lead.property_type} highlight={lead.property_type === 'Vacant Land'} />
              
              {/* Vacant Land specific fields */}
              {lead.property_type === 'Vacant Land' ? (
                <>
                  <DetailItem label="County" value={lead.county} />
                  <DetailItem label="Parcel Number" value={lead.parcel_number} />
                  <DetailItem label="Acreage" value={lead.acreage} highlight />
                  <DetailItem label="Zoning Type" value={lead.zoning_type} />
                  <DetailItem label="Road Access" value={lead.road_access} />
                  <DetailItem label="Flood Zone" value={lead.flood_zone} />
                  <DetailItem label="Annual Taxes" value={lead.annual_taxes} />
                  <DetailItem label="HOA" value={lead.has_hoa} />
                  <DetailItem label="GPS Coordinates" value={lead.gps_coordinates} />
                </>
              ) : (
                <>
                  <DetailItem label="Occupancy Status" value={lead.occupancy_status} />
                  <DetailItem label="Bedrooms" value={lead.bedrooms} />
                  <DetailItem label="Bathrooms" value={lead.bathrooms} />
                </>
              )}
              <DetailItem label="Mortgage Status" value={lead.mortgage_status} />
            </div>
            
            {/* Utilities for vacant land */}
            {lead.property_type === 'Vacant Land' && lead.utilities_available && lead.utilities_available.length > 0 && (
              <div className="mt-3">
                <span className="text-[#A3A3A3] text-sm">Utilities Available:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {lead.utilities_available.map((utility) => (
                    <span key={`utility-${utility}`} className="px-2 py-1 text-xs bg-green-500/20 text-green-400 border border-green-500/30">
                      {utility}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Property Condition (Houses) / Land Description (Land) */}
          {lead.property_type === 'Vacant Land' ? (
            lead.land_description && (
              <div>
                <h3 className="text-[#D4AF37] font-semibold mb-3">Land Description</h3>
                <p className="text-[#A3A3A3] text-sm bg-[#0A0A0A] p-4 border border-white/10">
                  {lead.land_description}
                </p>
              </div>
            )
          ) : (
            <div>
              <h3 className="text-[#D4AF37] font-semibold mb-3">Property Condition</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <DetailItem label="Condition" value={lead.property_condition} />
                <DetailItem label="Recently Updated" value={lead.recently_updated} />
              </div>
              {lead.major_issues && lead.major_issues.length > 0 && (
                <div className="mt-3">
                  <span className="text-[#A3A3A3] text-sm">Major Issues:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {lead.major_issues.map((issue) => (
                      <span key={`issue-${issue}`} className="px-2 py-1 text-xs bg-red-500/20 text-red-400 border border-red-500/30">
                        {issue}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Seller Motivation */}
          <div>
            <h3 className="text-[#D4AF37] font-semibold mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Seller Motivation
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <DetailItem label="Timeline" value={lead.timeline} />
              <DetailItem label="Listed with Agent" value={lead.listed_with_agent} />
              <DetailItem label="Asking Price" value={lead.asking_price ? `$${lead.asking_price}` : 'Make an offer'} />
              <DetailItem label="Motivation Score" value={`${lead.motivation_score}%`} />
            </div>
            
            {lead.selling_reasons && lead.selling_reasons.length > 0 && (
              <div className="mt-3">
                <span className="text-[#A3A3A3] text-sm">Reasons for Selling:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {lead.selling_reasons.map((reason) => (
                    <span key={`reason-${reason}`} className="px-2 py-1 text-xs bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30">
                      {reason}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {lead.offer_preferences && lead.offer_preferences.length > 0 && (
              <div className="mt-3">
                <span className="text-[#A3A3A3] text-sm">Deal Types Open To:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {lead.offer_preferences.map((pref) => (
                    <span key={`pref-${pref}`} className="px-2 py-1 text-xs bg-green-500/20 text-green-400 border border-green-500/30">
                      {pref}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Final Details */}
          <div>
            <h3 className="text-[#D4AF37] font-semibold mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Final Details
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <DetailItem 
                label="Preferred Contact Method" 
                value={Array.isArray(lead.preferred_contact) ? lead.preferred_contact.join(', ') : lead.preferred_contact} 
              />
              <DetailItem 
                label="Best Time to Contact" 
                value={Array.isArray(lead.best_time_to_contact) ? lead.best_time_to_contact.join(', ') : lead.best_time_to_contact || 'N/A'} 
              />
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[#A3A3A3] text-xs">Consent Given:</span>
              {lead.consent_given ? (
                <span className="flex items-center gap-1 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" /> Yes
                </span>
              ) : (
                <span className="text-red-400 text-sm">No</span>
              )}
            </div>
            
            {lead.additional_info && lead.additional_info !== 'No' && (
              <div className="mt-3 p-3 bg-[#0A0A0A] border border-white/10">
                <span className="text-[#A3A3A3] text-sm block mb-1">Additional Information:</span>
                <p className="text-white text-sm">{lead.additional_info}</p>
              </div>
            )}
          </div>

          {/* Property Photos */}
          {((lead.photos && lead.photos.length > 0) || lead.video_storage_path || lead.video_link) && (
            <div>
              <h3 className="text-[#D4AF37] font-semibold mb-3 flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Property Photos & Video
                {lead.photos && lead.photos.length > 0 && (
                  <span className="text-[#A3A3A3] text-sm font-normal">({lead.photos.length} photos)</span>
                )}
              </h3>
              
              {/* Photo Grid */}
              {lead.photos && lead.photos.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#A3A3A3] text-sm">Photos</span>
                    <a
                      href={`${API}/questionnaire/${lead.id}/photos/download`}
                      className="flex items-center gap-1 text-sm text-[#D4AF37] hover:underline"
                      download
                    >
                      <Download className="w-4 h-4" />
                      Download ZIP
                    </a>
                  </div>
                  <PhotoGallery photos={lead.photos} />
                </div>
              )}
              
              {/* Video */}
              {(lead.video_storage_path || lead.video_link) && (
                <div className="mt-3">
                  <span className="text-[#A3A3A3] text-sm block mb-2">Video Walkthrough:</span>
                  {lead.video_link ? (
                    <a
                      href={lead.video_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[#D4AF37] hover:underline"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View External Video
                    </a>
                  ) : lead.video_storage_path && (
                    <div className="flex items-center gap-2 text-white">
                      <Video className="w-4 h-4 text-[#D4AF37]" />
                      <span>Video uploaded</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Submission Info */}
          <div className="pt-3 border-t border-white/10">
            <div className="flex items-center gap-3 text-[#A3A3A3] text-sm">
              <Clock className="w-4 h-4" />
              <span>Submitted: {formatDate(lead.created_at)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Notes Section */}
      {showNotes && (
        <div className="border-t border-white/10 pt-4 mt-4">
          <label className="text-sm text-[#A3A3A3] mb-2 block">Notes</label>
          <div className="flex gap-2">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this lead..."
              className="flex-1 bg-[#0A0A0A] border border-white/20 text-white p-3 text-sm min-h-[80px] focus:border-[#D4AF37] focus:outline-none resize-y"
              data-testid={`notes-input-${lead.id}`}
            />
            <button
              onClick={handleSaveNotes}
              disabled={isSaving}
              className="px-4 py-2 bg-[#D4AF37] hover:bg-[#E5C158] text-black font-medium flex items-center gap-2 h-fit disabled:opacity-50"
              data-testid={`save-notes-${lead.id}`}
            >
              <Save className="w-4 h-4" />
              {isSaving ? "..." : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailItem = ({ label, value, highlight }) => {
  if (!value) return null;
  return (
    <div>
      <span className="text-[#A3A3A3] text-xs block">{label}</span>
      <span className={highlight ? "text-[#D4AF37] font-semibold" : "text-white"}>{value}</span>
    </div>
  );
};

export default AdminLeads;
